import {ReactNode} from 'react';
import {Navigate} from 'react-router-dom';
import {PlatformLoader} from '@flexinet/ui-components';
import {useVerifiedPermissions} from '../hooks/useVerifiedPermissions.ts';

// FNP-1203: the permission-gated half of the deep-link bug.
//
// RequireAuth already holds while the Cognito session is unresolved. One layer
// below it, every feature layout gated its routes on
// `allowedActions.includes(...)` — and VerifiedPermissionsProvider initialises
// allowedActions to [] and fills it from a useEffect. So on a hard load the
// check was false for at least one render, the layout fired <Navigate/>, and
// PublicHomePage forwarded on to /dashboard. That is why typing /registrars
// bounced while /zones/:zoneId (a placeholder rendered directly under the
// guard, with no layout in between) did not.
//
// Same shape as the auth bug: not-yet-loaded was indistinguishable from
// not-allowed. The provider already carried `authorizing` for exactly this and
// nothing consumed it — this component is the consumer.
//
// Deliberately NOT reusing AllowedRouteElement: that one infers loading from
// `allowedActions.length === 0`, which reads a genuinely unprivileged user as
// perpetually loading and parks them on a spinner. `authorizing` is the honest
// signal, and it distinguishes the two.
export const AuthorizedRouteElement = (
    {
        rbacAction,
        redirectTo,
        children,
    }: {
        rbacAction: string,
        redirectTo: string,
        children: ReactNode,
    }
) => {
    const {
        authorizing,
        allowedActions,
    } = useVerifiedPermissions();

    if (authorizing) {
        return <PlatformLoader/>;
    }

    if (!allowedActions.includes(rbacAction)) {
        return <Navigate to={redirectTo} replace/>;
    }

    return children;
};
